import { Stop } from "@api/graphql/generated/generated-types";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Radio, Tooltip } from "@src/common/components";
import StopCard from "@src/common/components/StopCard/StopCard";
import { compareStopsForDisplay } from "@src/shipments/utils/stopOrdering";
import { ForceAttemptModalStop } from "../hooks/useForceAttemptModalShipment";

interface ForceAttemptStopPickerProps {
  stops: ForceAttemptModalStop[];
  selectedStopId: string | null;
  onSelectStop: (stopId: string) => void;
}

interface CompletedStopRowProps {
  stop: ForceAttemptModalStop;
}

// Same "completed" affordance ForceCompleteModal uses, so a dispatcher sees
// the two modals render "completed" identically.
function CompletedStopRow({ stop }: CompletedStopRowProps) {
  return (
    <div className="flex min-w-0 items-center">
      <Tooltip content="Completed">
        <div className="h-4 w-4 shrink-0 rounded-full bg-green-300">
          <CheckIcon className="h-4 w-4 text-white" />
        </div>
      </Tooltip>
      <div className="min-w-0 flex-1 overflow-hidden">
        <StopCard stop={stop as Stop} />
      </div>
    </div>
  );
}

export function ForceAttemptStopPicker({
  stops,
  selectedStopId,
  onSelectStop,
}: ForceAttemptStopPickerProps) {
  const orderedStops: ForceAttemptModalStop[] = [...stops].sort(
    compareStopsForDisplay
  );
  const attemptableStops: ForceAttemptModalStop[] = orderedStops.filter(
    (stop) => !stop.completedAt
  );
  const completedStops: ForceAttemptModalStop[] = orderedStops.filter(
    (stop) => stop.completedAt
  );

  // One open stop needs no choice — show it as context, not as a decision.
  // Completed stops still render below it so the dispatcher sees the whole order.
  if (attemptableStops.length === 1)
    return (
      <div className="space-y-2">
        <div className="flex min-w-0 items-center">
          <div className="min-w-0 flex-1 overflow-hidden">
            <StopCard stop={attemptableStops[0] as Stop} />
          </div>
        </div>
        {completedStops.map((stop) => (
          <CompletedStopRow key={stop.id} stop={stop} />
        ))}
      </div>
    );

  return (
    <div className="space-y-2">
      {orderedStops.map((stop) =>
        stop.completedAt ? (
          <CompletedStopRow key={stop.id} stop={stop} />
        ) : (
          // min-w-0 + flex-1 around StopCard is load-bearing: StopCard is
          // w-full, so beside a fixed-width radio it demands the row's full
          // width and overflows this narrow two-column layout, sliding under
          // the consequence panel instead of ellipsizing. ForceCompleteModal
          // has the same markup but is single-column and wide, so it never
          // shows there.
          <div key={stop.id} className="flex min-w-0 items-center">
            <Radio
              className="shrink-0"
              id={`force-attempt-stop-${stop.id}`}
              checked={selectedStopId === stop.id}
              onChange={() => onSelectStop(stop.id)}
            />
            <div className="min-w-0 flex-1 overflow-hidden">
              <StopCard stop={stop as Stop} />
            </div>
          </div>
        )
      )}
    </div>
  );
}
